import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagopsService } from '../services/tagopsService';
import { getMaxAccountsFromSubscription } from '../utils/subscriptionUtils';
import { useInventory } from '../contexts/InventoryContext';
import { useConfig } from '../contexts/ConfigContext';
import { TagRule, TagTemplate } from '../types/tagops';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  InputAdornment,
} from '@mui/material';
import {
  Cloud as CloudIcon,
  Rule as RuleIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  ViewColumn as ViewColumnIcon,
  ViewList as ViewListIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import { ResponsiveContainer, Tooltip as RechartsTooltip, Treemap, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, SankeyNode, SankeyLink } from 'd3-sankey';

// D3 Sankey Chart Component
interface SankeyData {
  nodes: { name: string; color?: string; regionBreakdown?: Record<string, number> }[];
  links: { source: number; target: number; value: number }[];
}

interface D3SankeyChartProps {
  data: SankeyData;
  width?: number;
  height?: number;
}

function D3SankeyChart({ 
  data, 
  width = 800, 
  height = 400 
}: D3SankeyChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear previous content
    d3.select(svgRef.current).selectAll('*').remove();

    const margin = { top: 10, right: 10, bottom: 10, left: 10 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const svg = d3
      .select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Create sankey generator
    const sankeyGenerator = sankey<SankeyNode<any, any>, SankeyLink<any, any>>()
      .nodeWidth(15)
      .nodePadding(20)
      .extent([
        [1, 1],
        [innerWidth - 1, innerHeight - 5],
      ]);

    // Generate sankey layout
    const { nodes, links } = sankeyGenerator({
      nodes: data.nodes.map(d => ({ ...d })),
      links: data.links.map(d => ({ ...d })),
    });

    // Color scale
    const colorScale = d3.scaleOrdinal(d3.schemeCategory10);

    // Add links
    const link = svg
      .append('g')
      .selectAll('path')
      .data(links)
      .join('path')
      .attr('d', sankeyLinkHorizontal())
      .attr('stroke', (d: any) => {
        const sourceColor = data.nodes[d.source.index]?.color || colorScale(d.source.index.toString());
        return sourceColor;
      })
      .attr('stroke-width', (d: any) => Math.max(2, d.width))
      .attr('fill', 'none')
      .attr('opacity', 0.6)
      .on('mouseenter', function(event: any, d: any) {
        d3.select(this as SVGPathElement).attr('opacity', 0.9);
      })
      .on('mouseleave', function(event: any, d: any) {
        d3.select(this as SVGPathElement).attr('opacity', 0.6);
      });

    // Add link titles (tooltips)
    link.append('title').text((d: any) => {
      return `${d.source.name} → ${d.target.name}\n${d.value.toLocaleString()} resources`;
    });

    // Add nodes
    const node = svg
      .append('g')
      .selectAll('rect')
      .data(nodes)
      .join('rect')
      .attr('x', (d: any) => d.x0)
      .attr('y', (d: any) => d.y0)
      .attr('height', (d: any) => d.y1 - d.y0)
      .attr('width', (d: any) => d.x1 - d.x0)
      .attr('fill', (d: any) => data.nodes[d.index]?.color || colorScale(d.index.toString()))
      .attr('opacity', 0.8)
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .attr('rx', 3)
      .on('mouseenter', function(event: any, d: any) {
        setHoveredNode(d.name);
        d3.select(this as SVGRectElement).attr('opacity', 1);
      })
      .on('mouseleave', function(event: any, d: any) {
        setHoveredNode(null);
        d3.select(this as any).attr('opacity', 0.8);
      });

    // Add node titles (tooltips) with region breakdown
    node.append('title').text((d: any) => {
      const nodeData = data.nodes[d.index];
      let tooltipText = `${d.name}\n${d.value?.toLocaleString() || 0} resources`;
      
      if (nodeData?.regionBreakdown && Object.keys(nodeData.regionBreakdown).length > 0) {
        tooltipText += '\n\nBy Region:';
        Object.entries(nodeData.regionBreakdown)
          .sort(([,a], [,b]) => b - a)
          .forEach(([region, count]) => {
            tooltipText += `\n${region}: ${count.toLocaleString()}`;
          });
      }
      
      return tooltipText;
    });

    // Add node labels with better readability for dark mode only
    svg
      .append('g')
      .selectAll('text')
      .data(nodes)
      .join('text')
      .attr('x', (d: any) => (d.x0 < innerWidth / 2 ? d.x1 + 6 : d.x0 - 6))
      .attr('y', (d: any) => (d.y1 + d.y0) / 2)
      .attr('dy', '0.35em')
      .attr('text-anchor', (d: any) => (d.x0 < innerWidth / 2 ? 'start' : 'end'))
      .attr('fill', theme.palette.mode === 'dark' ? '#ffffff' : '#1e293b')
      .attr('font-size', '14px')
      .attr('font-weight', 'bold')
      .attr('text-shadow', theme.palette.mode === 'dark' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none')
      .attr('paint-order', theme.palette.mode === 'dark' ? 'stroke fill' : 'normal')
      .attr('stroke', theme.palette.mode === 'dark' ? '#000000' : 'none')
      .attr('stroke-width', theme.palette.mode === 'dark' ? '2' : '0')
      .text((d: any) => d.name)
      .append('tspan')
      .attr('x', (d: any) => (d.x0 < innerWidth / 2 ? d.x1 + 6 : d.x0 - 6))
      .attr('dy', '1.2em')
      .attr('fill', theme.palette.mode === 'dark' ? '#ffffff' : '#000000')
      .attr('font-size', '11px')
      .attr('text-shadow', theme.palette.mode === 'dark' ? '2px 2px 4px rgba(0,0,0,0.8)' : 'none')
      .attr('paint-order', theme.palette.mode === 'dark' ? 'stroke fill' : 'normal')
      .attr('stroke', theme.palette.mode === 'dark' ? '#000000' : 'none')
      .attr('stroke-width', theme.palette.mode === 'dark' ? '2' : '0')
      .text((d: any) => `${d.value?.toLocaleString() || 0}`);

  }, [data, width, height, theme]);

  return (
    <Box sx={{ height: height }}>
      <div style={{ overflowX: 'auto' }}>
        <svg ref={svgRef} />
      </div>
      {hoveredNode && (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="body2" color="text.secondary">
            Hovering: <span style={{ color: '#1e293b' }}>{hoveredNode}</span>
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Service icon mapping function (same as Inventory page)
const getServiceIcon = (service: string): string => {
  const iconMap: Record<string, string> = {
    // Compute services
    'ec2': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_Amazon-EC2_48.svg',
    'lambda': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Lambda_48.svg',
    'elasticbeanstalk': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Elastic-Beanstalk_48.svg',
    'EC2': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_Amazon-EC2_48.svg',
    'Lambda': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Lambda_48.svg',
    'ElasticBeanstalk': '/icons/Architecture-Service-Icons_07312025/Arch_Compute/48/Arch_AWS-Elastic-Beanstalk_48.svg',
    
    // Storage services
    's3': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Simple-Storage-Service_48.svg',
    'ebs': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Elastic-Block-Store_48.svg',
    'efs': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'elasticfilesystem': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'ELASTICFILESYSTEM': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    'S3': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Simple-Storage-Service_48.svg',
    'EBS': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-Elastic-Block-Store_48.svg',
    'EFS': '/icons/Architecture-Service-Icons_07312025/Arch_Storage/48/Arch_Amazon-EFS_48.svg',
    
    // Database services
    'rds': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-RDS_48.svg',
    'dynamodb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DynamoDB_48.svg',
    'dax': '/icons/Resource-Icons_07312025/Res_Database/Res_Amazon-DynamoDB_Amazon-DynamoDB-Accelerator_48.svg',
    'documentdb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DocumentDB_48.svg',
    'docdb': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-DocumentDB_48.svg',
    'docdb-elastic': '/icons/Resource-Icons_07312025/Res_Database/Res_Amazon-DocumentDB_Elastic-Clusters_48.svg',
    'aurora': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'aurora-dsql': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'dsql': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Aurora_48.svg',
    'timestream': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-Timestream_48.svg',
    'redshift': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Redshift_48.svg',
    'elasticache': '/icons/Architecture-Service-Icons_07312025/Arch_Database/48/Arch_Amazon-ElastiCache_48.svg',
    
    // Networking services
    'vpc': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Virtual-Private-Cloud_48.svg',
    'route53': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'route53profiles': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'route53resolver': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-Route-53_48.svg',
    'cloudfront': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-CloudFront_48.svg',
    'apigateway': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Amazon-API-Gateway_48.svg',
    'elasticloadbalancing': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_Elastic-Load-Balancing_48.svg',
    'directconnect': '/icons/Architecture-Service-Icons_07312025/Arch_Networking-Content-Delivery/48/Arch_AWS-Direct-Connect_48.svg',
    
    // Security services
    'iam': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Identity-and-Access-Management_48.svg',
    'kms': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Key-Management-Service_48.svg',
    'securityhub': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_AWS-Security-Hub_48.svg',
    'macie': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Macie_48.svg',
    'macie2': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Macie_48.svg',
    
    // Application Integration
    'sns': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-Simple-Notification-Service_48.svg',
    'sqs': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-Simple-Queue-Service_48.svg',
    'eventbridge': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'events': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'event': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'eventbridge-rule': '/icons/Resource-Icons_07312025/Res_Application-Integration/Res_Amazon-EventBridge_Rule_48.svg',
    'pipes': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-EventBridge_48.svg',
    'scheduler': '/icons/Resource-Icons_07312025/Res_Application-Integration/Res_Amazon-EventBridge_Scheduler_48.svg',
    'mq': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-MQ_48.svg',
    'amazonmq': '/icons/Architecture-Service-Icons_07312025/Arch_App-Integration/48/Arch_Amazon-MQ_48.svg',
    
    // Business Applications
    'ses': '/icons/Architecture-Service-Icons_07312025/Arch_Business-Applications/48/Arch_Amazon-Simple-Email-Service_48.svg',
    'amplify': '/icons/Architecture-Service-Icons_07312025/Arch_Front-End-Web-Mobile/48/Arch_AWS-Amplify_48.svg',
    
    // Management & Governance
    'cloudwatch': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    'logs': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    'cloudtrail': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-CloudTrail_48.svg',
    'config': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Config_48.svg',
    'ssm': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_AWS-Systems-Manager_48.svg',
    'synthetics': '/icons/Architecture-Service-Icons_07312025/Arch_Management-Governance/48/Arch_Amazon-CloudWatch_48.svg',
    
    // Containers
    'ecs': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Container-Service_48.svg',
    'eks': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Kubernetes-Service_48.svg',
    'ecr': '/icons/Architecture-Service-Icons_07312025/Arch_Containers/48/Arch_Amazon-Elastic-Container-Registry_48.svg',
    
    // Developer Tools
    'codebuild': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeBuild_48.svg',
    'codedeploy': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeDeploy_48.svg',
    'codeartifact': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodeArtifact_48.svg',
    'codepipeline': '/icons/Architecture-Service-Icons_07312025/Arch_Developer-Tools/48/Arch_AWS-CodePipeline_48.svg',
    
    // Analytics
    'athena': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Athena_48.svg',
    'kinesis': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis_48.svg',
    'kinesis-data-streams': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis-Data-Streams_48.svg',
    'firehose': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Data-Firehose_48.svg',
    'kinesis-firehose': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Data-Firehose_48.svg',
    'kinesisanalytics': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Service-for-Apache-Flink_48.svg',
    'flink': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Service-for-Apache-Flink_48.svg',
    'kinesisvideo': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Kinesis-Video-Streams_48.svg',
    'emr': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'elasticmapreduce': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'emr-serverless': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'emr-containers': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-EMR_48.svg',
    'opensearch': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'es': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'opensearchserverless': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'aoss': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-OpenSearch-Service_48.svg',
    'msk': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Streaming-for-Apache-Kafka_48.svg',
    'kafka': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-Managed-Streaming-for-Apache-Kafka_48.svg',
    'kafka-connect': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-MSK_Amazon-MSK-Connect_48.svg',
    'mskconnect': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-MSK_Amazon-MSK-Connect_48.svg',
    'osis': '/icons/Resource-Icons_07312025/Res_Analytics/Res_Amazon-OpenSearch-Service_OpenSearch-Ingestion_48.svg',
    'sagemaker': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_Amazon-SageMaker_48.svg',
    'glue': '/icons/Architecture-Service-Icons_07312025/Arch_Analytics/48/Arch_AWS-Glue_48.svg',
    
    // AI/ML
    'cognito': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'cognito-identity': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'cognito-idp': '/icons/Architecture-Service-Icons_07312025/Arch_Security-Identity-Compliance/48/Arch_Amazon-Cognito_48.svg',
    'bedrock': '/icons/Architecture-Service-Icons_07312025/Arch_Artificial-Intelligence/48/Arch_Amazon-Bedrock_48.svg',
    'rekognition': '/icons/Architecture-Service-Icons_07312025/Arch_Artificial-Intelligence/48/Arch_Amazon-Rekognition_48.svg',
    
    // S3 Control
    's3-control': '/icons/Resource-Icons_07312025/Res_Storage/Res_Amazon-Simple-Storage-Service_S3-Replication-Time-Control_48.svg',
    's3control': '/icons/Resource-Icons_07312025/Res_Storage/Res_Amazon-Simple-Storage-Service_S3-Replication-Time-Control_48.svg',
  };
  
  // Try exact match first
  if (iconMap[service]) {
    return iconMap[service];
  }
  
  // Try lowercase match
  const lowerService = service.toLowerCase();
  if (iconMap[lowerService]) {
    return iconMap[lowerService];
  }
  
  // Try to find a partial match for common patterns
  const partialMatches = Object.keys(iconMap).filter(key => 
    key.toLowerCase().includes(lowerService) || 
    lowerService.includes(key.toLowerCase())
  );
  
  if (partialMatches.length > 0) {
    return iconMap[partialMatches[0]];
  }
  
  // Default fallback
  return '/icons/Architecture-Service-Icons_07312025/Arch_General-Icons/48/Arch_AWS-Cloud_48.svg';
};

export const Overview: React.FC = () => {
  const navigate = useNavigate();
  const { inventoryData, loading: inventoryLoading, refreshInventory } = useInventory();
  const { serviceConfig, accountsData, loading: configLoading, refreshServiceConfig, refreshAccounts } = useConfig();
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Separate loading states for each widget
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingTreemap, setLoadingTreemap] = useState(true);
  const [loadingSankey, setLoadingSankey] = useState(false);
  const [loadingTimeline, setLoadingTimeline] = useState(true);
  const [loadingTagKeyDistribution, setLoadingTagKeyDistribution] = useState(false);

  // Real data for dashboard statistics
  const [dashboardStats, setDashboardStats] = useState({
    totalRules: 0,
    activeRules: 0,
    totalResources: 0,
    taggedResources: 0,
    scanStatus: 'disabled' as 'enabled' | 'disabled' | 'running' | 'failed',
    scanSchedule: 'Not scheduled',
    builtInTemplates: 22, // Static - we know we have 22 built-in templates
    customTemplates: 0,
    regionsMonitored: 0,
    accountsConnected: 0,
    maxAccountsAllowed: null as number | null, // From subscription
  });

  // Resource categories data from inventory
  const [resourceCategories, setResourceCategories] = useState<Array<{
    service: string;
    count: number;
    icon: string;
  }>>([]);


  // Treemap data for resource categories
  const [treemapData, setTreemapData] = useState<Array<{
    name: string;
    size: number;
    fill: string;
    icon: string;
  }>>([]);

  // Sankey chart data for resources by account
  const [sankeyData, setSankeyData] = useState<{
    nodes: Array<{ name: string }>;
    links: Array<{ source: number; target: number; value: number }>;
  }>({ nodes: [], links: [] });

  // Timeline data for resource count over time
  const [rawTimelineData, setRawTimelineData] = useState<Array<{
    date: string;
    total_count: number;
    [serviceName: string]: string | number; // Dynamic service counts
  }>>([]);
  const [timelineData, setTimelineData] = useState<Array<{
    date: string;
    total_count: number;
    [serviceName: string]: string | number; // Dynamic service counts
  }>>([]);

  // Tag key distribution data - count of resources per tag key
  const [tagKeyDistributionData, setTagKeyDistributionData] = useState<Array<{
    key: string;
    count: number;
    uniqueValues: number;
  }>>([]);

  // Tag value distribution data - for selected tag key
  const [selectedTagKey, setSelectedTagKey] = useState<string>('');
  const [tagValueDistributionData, setTagValueDistributionData] = useState<Array<{
    value: string;
    count: number;
  }>>([]);

  // Naming convention status breakdown (valid, invalid, not_defined)
  const [namingStatusDistributionData, setNamingStatusDistributionData] = useState<Array<{
    status: string;
    label: string;
    count: number;
    fill: string;
  }>>([]);

  // Ref for scrolling to Tag Value Distribution section
  const tagValueDistributionRef = useRef<HTMLDivElement>(null);

  // Timeline filter states (now supporting multi-select)
  const [selectedTimelineService, setSelectedTimelineService] = useState<string[]>(['all']);
  const [selectedTimelinePeriod, setSelectedTimelinePeriod] = useState<string>('all');

  // CSV parsing utility function
  const parseTimelineCSV = (csvData: string): Array<{
    date: string;
    total_count: number;
    [serviceName: string]: string | number;
  }> => {
    const lines = csvData.trim().split('\r\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',');
    const dataRows = lines.slice(1);
    
    return dataRows.map(row => {
      const values = row.split(',');
      const timestamp = values[0];
      const totalCount = parseInt(values[1]) || 0;
      
      // Create object with timestamp, total_count, and individual service counts
      const timelineEntry: any = {
        date: timestamp,
        total_count: totalCount,
      };
      
      // Add each service count (skip timestamp and total_count columns)
      for (let i = 2; i < headers.length && i < values.length; i++) {
        const serviceName = headers[i];
        const serviceCount = parseInt(values[i]) || 0;
        timelineEntry[serviceName] = serviceCount;
      }
      
      return timelineEntry;
    });
  };

  // Fullscreen modal state for Sankey chart
  const [sankeyFullscreen, setSankeyFullscreen] = useState(false);
  
  // Toggle between 3-column and 4-column Sankey chart
  const [showServices, setShowServices] = useState(true);

  // Global filter states for both graphs (now supporting multi-select)
  const [selectedAccount, setSelectedAccount] = useState<string[]>(['all']);
  const [selectedRegion, setSelectedRegion] = useState<string[]>(['all']);
  // Resource Distribution Flow: service filter defaults to "all"
  const [selectedService, setSelectedService] = useState<string[]>(['all']);
  const [selectedTags, setSelectedTags] = useState<string[]>([]); // Store as "key: value" display strings
  const [availableAccounts, setAvailableAccounts] = useState<Array<{id: string, name: string}>>([]);
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [availableServices, setAvailableServices] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Array<{key: string, value: string, display: string}>>([]);




  // Store API response data separately to avoid re-fetching
  const [apiData, setApiData] = useState<{
    rulesData: Record<string, TagRule>;
    templatesData: Record<string, TagTemplate>;
    scanSettings: { status: string; cron: string; timezone: string };
    maxAccounts: number | null;
  } | null>(null);

  // Widget 1: Load Stats (Rules, Scan Settings, Subscription) - Independent
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoadingStats(true);
        
        // Load rules, scan settings, and subscription data in parallel
        const [ruleResponse, scanSettingsResponse, subscriptionResponse] = await Promise.all([
          tagopsService.getRuleConfig(),
          tagopsService.getScanSettings().catch(() => ({ data: { status: 'DISABLED', cron: '15 3 * * ? *' } })),
          tagopsService.getSubscriptionStatus().catch(() => ({ subscription: null }))
        ]);

        // Parse rules data
        const rulesData: Record<string, TagRule> = {};
        if (ruleResponse.rules && ruleResponse.rules.rules && typeof ruleResponse.rules.rules === 'object') {
          Object.entries(ruleResponse.rules.rules as Record<string, any>).forEach(([ruleName, ruleData]) => {
            if (ruleData && typeof ruleData === 'object') {
              rulesData[ruleName] = ruleData as TagRule;
            }
          });
        }

        // Parse templates data
        const templatesData: Record<string, TagTemplate> = {};
        if (ruleResponse.rules && ruleResponse.rules.templates) {
          ruleResponse.rules.templates.forEach((templateObj: any) => {
            if (typeof templateObj === 'object' && templateObj !== null) {
              Object.entries(templateObj).forEach(([templateName, templateData]) => {
                templatesData[templateName] = templateData as TagTemplate;
              });
            }
          });
        }

        // Parse scan settings data
        const scanSettings = scanSettingsResponse?.data || { status: 'DISABLED', cron: '15 3 * * ? *', timezone: 'UTC' };

        // Extract max accounts from subscription (tiered, Fixed, AWS Marketplace trial, or unlimited)
        const maxAccounts = getMaxAccountsFromSubscription(subscriptionResponse?.subscription);

        // Store API data
        setApiData({
          rulesData,
          templatesData,
          scanSettings,
          maxAccounts,
        });

      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoadingStats(false);
      }
    };

    loadStats();
  }, [lastRefresh]);

  // Widget 2: Load Service Resource Breakdown (Treemap) - Independent, uses new count endpoint
  useEffect(() => {
    const loadTreemapData = async () => {
      try {
        setLoadingTreemap(true);
        
        // Use the new count endpoint for faster loading
        const countResponse = await tagopsService.getCountSettings().catch((err) => {
          console.error('Error fetching count settings:', err);
          return { data: {} };
        });
        
        const countData = countResponse?.data || {};
        
        // Debug: log the data structure
        if (Object.keys(countData).length === 0) {
          console.warn('No count data received from API');
        }
        
        // Convert count data to treemap format
        const categories = Object.entries(countData)
          .filter(([key]) => {
            // Exclude metadata fields
            return key !== 'tenantId' && key !== 'ttu' && key !== 'value' && key !== 'type';
          })
          .map(([service, countStr]) => {
            const count = parseInt(countStr as string, 10) || 0;
            return {
              service: service.toUpperCase(),
              count: count,
              icon: getServiceIcon(service)
            };
          })
          .filter(cat => cat.count > 0) // Only include services with resources
          .sort((a, b) => b.count - a.count);

        setResourceCategories(categories);

        // Transform to treemap format
        if (categories.length > 0) {
          const maxCount = Math.max(...categories.map(c => c.count), 1);
          const treemapCategories = categories.map((category, index) => {
            const ratio = category.count / maxCount;
            const hueBase = (index * 45) % 360;
            const hue = hueBase < 60 ? hueBase + 200 : hueBase;
            const saturation = 50 + (ratio * 25);
            const lightness = 70 - (ratio * 20);
            
            return {
              name: category.service,
              size: category.count,
              fill: `hsl(${hue}, ${saturation}%, ${lightness}%)`,
              icon: category.icon
            };
          });
          
          setTreemapData(treemapCategories);
        } else {
          setTreemapData([]);
        }

      } catch (error) {
        console.error('Error loading treemap data:', error);
        setTreemapData([]);
      } finally {
        setLoadingTreemap(false);
      }
    };

    loadTreemapData();
  }, [lastRefresh]);

  // Widget 3: Load Timeline - Independent
  useEffect(() => {
    const loadTimeline = async () => {
      try {
        setLoadingTimeline(true);
        
        const timelineResponse = await tagopsService.getTimeline().catch(() => ({ data: '' }));
        const timelineCSV = (timelineResponse as any)?.data || '';
        const rawTimeline = timelineCSV ? parseTimelineCSV(timelineCSV) : [];
        
        setRawTimelineData(rawTimeline);

      } catch (error) {
        console.error('Error loading timeline:', error);
      } finally {
        setLoadingTimeline(false);
      }
    };

    loadTimeline();
  }, [lastRefresh]);

  // Widget 4: Process Tag Key Distribution - Independent, depends on inventory
  useEffect(() => {
    if (!inventoryData?.resources) {
      setLoadingTagKeyDistribution(false);
      return;
    }

    const processTagKeyDistribution = () => {
      try {
        setLoadingTagKeyDistribution(true);
        
        const resources = inventoryData.resources || [];
        const tagKeyCounts: Record<string, { count: number; uniqueValues: Set<string> }> = {};
        
        resources.forEach(resource => {
          if (resource.tags) {
            Object.entries(resource.tags).forEach(([key, value]) => {
              if (!tagKeyCounts[key]) {
                tagKeyCounts[key] = { count: 0, uniqueValues: new Set() };
              }
              tagKeyCounts[key].count += 1;
              if (value) {
                tagKeyCounts[key].uniqueValues.add(value);
              }
            });
          }
        });

        const tagKeyDistribution = Object.entries(tagKeyCounts)
          .map(([key, data]) => ({
            key,
            count: data.count,
            uniqueValues: data.uniqueValues.size
          }))
          .sort((a, b) => b.count - a.count);
        
        setTagKeyDistributionData(tagKeyDistribution);

        // Extract available tags for filtering
        const tagSet = new Set<string>();
        resources.forEach(resource => {
          if (resource.tags) {
            Object.entries(resource.tags).forEach(([key, value]) => {
              if (key && value) {
                tagSet.add(`${key}=${value}`);
              }
            });
          }
        });
        
        const uniqueTags = Array.from(tagSet)
          .map(tagStr => {
            const [key, ...valueParts] = tagStr.split('=');
            const value = valueParts.join('=');
            return {
              key,
              value,
              display: `${key}: ${value}`
            };
          })
          .sort((a, b) => {
            const keyCompare = a.key.localeCompare(b.key);
            if (keyCompare !== 0) return keyCompare;
            return a.value.localeCompare(b.value);
          });
        
        setAvailableTags(uniqueTags);

      } catch (error) {
        console.error('Error processing tag key distribution:', error);
      } finally {
        setLoadingTagKeyDistribution(false);
      }
    };

    processTagKeyDistribution();
  }, [inventoryData]);

  // Widget: Naming convention status breakdown (valid, invalid, not_defined)
  useEffect(() => {
    if (!inventoryData?.resources) {
      setNamingStatusDistributionData([]);
      return;
    }
    const resources = inventoryData.resources as Array<{ naming_convention_status?: string }>;
    const counts: Record<string, number> = { valid: 0, invalid: 0, not_defined: 0 };
    resources.forEach((r) => {
      const s = (r.naming_convention_status || 'not_defined').toLowerCase();
      if (s === 'valid') counts.valid += 1;
      else if (s === 'invalid') counts.invalid += 1;
      else counts.not_defined += 1;
    });
    const colors: Record<string, string> = {
      valid: '#2e7d32',
      invalid: '#d32f2f',
      not_defined: '#9e9e9e',
    };
    const labels: Record<string, string> = {
      valid: 'Valid',
      invalid: 'Invalid',
      not_defined: 'Not defined',
    };
    const data = (['valid', 'invalid', 'not_defined'] as const)
      .map((status) => ({
        status,
        label: labels[status],
        count: counts[status],
        fill: colors[status],
      }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count);
    setNamingStatusDistributionData(data);
  }, [inventoryData]);

  // Process context data and update dashboard stats - depends on context data and API data
  useEffect(() => {
    if (!apiData) return; // Wait for API data to be loaded

    // Parse cron expression to get human-readable schedule.
    // Must match Settings.tsx: 5-field AWS-style, or 6-field EventBridge (minute hour day month day-of-week year).
    const parseCronToSchedule = (cron: string, timezone: string = 'UTC') => {
      if (!cron || cron.trim() === '') {
        return 'Not scheduled';
      }

      const parts = cron.trim().split(/\s+/);

      const scheduleFromParts = (
        minute: string,
        hour: string,
        day: string,
        _month: string,
        dayOfWeek: string
      ): string | null => {
        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);

        if (isNaN(hourNum) || isNaN(minuteNum) || hourNum < 0 || hourNum > 23 || minuteNum < 0 || minuteNum > 59) {
          return null;
        }

        const timeStr = `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;

        if (dayOfWeek === '?' && day === '*') {
          return `Daily at ${timeStr} (${timezone})`;
        }
        if (dayOfWeek === '?' && day !== '*') {
          return `Monthly on day ${day} at ${timeStr} (${timezone})`;
        }
        if (dayOfWeek !== '?' && dayOfWeek !== '*') {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const dayCodes = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

          if (dayOfWeek.includes(',')) {
            const selectedDays = dayOfWeek.split(',').map((d) => d.trim());
            const dayLabels = selectedDays.map((d) => {
              const dayIndex = dayCodes.indexOf(d);
              return dayIndex >= 0 ? dayNames[dayIndex] : d;
            });
            return `${dayLabels.join(', ')} at ${timeStr} (${timezone})`;
          }

          const dayIndex = dayCodes.indexOf(dayOfWeek);
          if (dayIndex >= 0) {
            return `${dayNames[dayIndex]} at ${timeStr} (${timezone})`;
          }
        }

        return `Daily at ${timeStr} (${timezone})`;
      };

      if (parts.length === 5) {
        const out = scheduleFromParts(parts[0], parts[1], parts[2], parts[3], parts[4]);
        return out ?? 'Not scheduled';
      }

      if (parts.length === 6) {
        // Primary: EventBridge / Scan settings UI — minute hour day month day-of-week year (ignore year)
        const eventBridge = scheduleFromParts(parts[0], parts[1], parts[2], parts[3], parts[4]);
        if (eventBridge) {
          return eventBridge;
        }

        // Legacy: Quartz-style second minute hour day month day-of-week
        const second = parts[0];
        const minute = parts[1];
        const hour = parts[2];
        const day = parts[3];

        const hourNum = parseInt(hour, 10);
        const minuteNum = parseInt(minute, 10);

        if (hour === '*' && minute !== '*' && second !== '*') {
          const mn = parseInt(minute, 10);
          const sn = parseInt(second, 10);
          if (!isNaN(mn) && !isNaN(sn) && mn >= 0 && mn <= 59 && sn >= 0 && sn <= 59) {
            const timeStr = `${mn.toString().padStart(2, '0')}:${sn.toString().padStart(2, '0')}`;
            return `Daily at ${timeStr} (${timezone})`;
          }
        }

        if (!isNaN(hourNum) && !isNaN(minuteNum) && hourNum >= 0 && hourNum <= 23 && minuteNum >= 0 && minuteNum <= 59) {
          const timeStr = `${hourNum.toString().padStart(2, '0')}:${minuteNum.toString().padStart(2, '0')}`;
          if (day === '*') {
            return `Daily at ${timeStr} (${timezone})`;
          }
          return `Monthly on day ${day} at ${timeStr} (${timezone})`;
        }
      }

      return 'Not scheduled';
    };

    // Use accounts data from context
    const accounts = accountsData?.accounts || [];
    
    // Calculate unique regions from accounts data
    const uniqueRegions = new Set<string>();
    accounts.forEach(account => {
      if (account.regions) {
        // Handle both array and comma-separated string formats
        let regions: string[] = [];
        if (Array.isArray(account.regions)) {
          regions = account.regions;
        } else {
          regions = account.regions.split(',').map((r: string) => r.trim()).filter((r: string) => r);
        }
        regions.forEach((region: string) => uniqueRegions.add(region));
      }
      if (account.allowed_regions) {
        // Handle both array and comma-separated string formats
        let regions: string[] = [];
        if (Array.isArray(account.allowed_regions)) {
          regions = account.allowed_regions;
        } else {
          regions = account.allowed_regions.split(',').map((r: string) => r.trim()).filter((r: string) => r);
        }
        regions.forEach((region: string) => uniqueRegions.add(region));
      }
      if (account.allowerd_regions) {
        // Handle typo in field name (allowerd_regions) - assume it's always a string
        const regions = account.allowerd_regions.split(',').map((r: string) => r.trim()).filter((r: string) => r);
        regions.forEach((region: string) => uniqueRegions.add(region));
      }
    });

    // Process resource categories and calculate tagged resources
    let taggedCount = 0;
    
    if (inventoryData) {
      // Populate filter options from inventory
      const allResourcesForFilters = inventoryData.resources || [];
      
      // Extract unique accounts
      const accounts = Array.from(new Set(allResourcesForFilters.map(item => item.account)))
        .filter(account => account && account.trim() !== '')
        .sort()
        .map(account => ({ id: account, name: account }));
      setAvailableAccounts(accounts);
      
      // Extract unique regions
      const regions = Array.from(new Set(allResourcesForFilters.map(item => item.region)))
        .filter(region => region && region.trim() !== '')
        .sort();
      setAvailableRegions(regions);
      
      // Extract unique services
      const services = Array.from(new Set(allResourcesForFilters.map(item => item.service)))
        .filter(service => service && service.trim() !== '')
        .sort();
      setAvailableServices(services);

      // Keep current selection, or default to "all" if empty
      setSelectedService(prev => (prev.length === 0 ? ['all'] : prev));

      // Calculate tagged vs untagged resources
      const resources = inventoryData.resources || [];
      resources.forEach(resource => {
        const hasTags = resource.tags && Object.keys(resource.tags).length > 0;
        if (hasTags) {
          taggedCount++;
        }
      });
    }

    // Update dashboard stats with real data
    setDashboardStats(prev => ({
      ...prev,
      totalRules: Object.keys(apiData.rulesData).length,
      activeRules: Object.values(apiData.rulesData).filter(rule => rule.enabled !== false).length,
      totalResources: inventoryData?.total_count || 0,
      taggedResources: taggedCount,
      customTemplates: Object.keys(apiData.templatesData).length, // All templates in API are custom
      accountsConnected: accounts.length,
      regionsMonitored: uniqueRegions.size,
      scanStatus: apiData.scanSettings.status.toLowerCase() as 'enabled' | 'disabled' | 'running' | 'failed',
      scanSchedule:
        (apiData.scanSettings.status || '').toUpperCase() === 'ENABLED'
          ? parseCronToSchedule(
              apiData.scanSettings.cron,
              apiData.scanSettings.timezone || 'UTC'
            )
          : 'Disabled',
      maxAccountsAllowed: apiData.maxAccounts,
    }));
  }, [apiData, inventoryData, accountsData]); // Process when API data or context data changes

  // Separate useEffect to filter timeline data when filters change
  useEffect(() => {
    if (rawTimelineData.length === 0) return;
    
    // Apply time-based filtering first
    let timeFilteredTimeline = rawTimelineData;
    if (selectedTimelinePeriod !== 'all') {
      const now = new Date();
      let cutoffDate: Date;
      
      switch (selectedTimelinePeriod) {
        case 'week':
          cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '2weeks':
          cutoffDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '3months':
          cutoffDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          cutoffDate = new Date(0); // Show all data
      }
      
      timeFilteredTimeline = rawTimelineData.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate >= cutoffDate;
      });
    }
    
    // Apply service-specific filters
    const filteredTimeline = timeFilteredTimeline.map(timelineEntry => {
      // Get selected services excluding "all"
      const selectedServices = selectedTimelineService.filter(service => service !== 'all');
      
      // If "all" is selected without other services, return original entry
      if (selectedTimelineService.includes('all') && selectedServices.length === 0) {
        return timelineEntry;
      }
      
      // Create filtered entry
      const filteredEntry: any = {
        date: timelineEntry.date,
        total_count: selectedTimelineService.includes('all') ? timelineEntry.total_count : 0,
      };
      
      // Add selected individual services
      selectedServices.forEach(service => {
        const serviceCount = timelineEntry[service] || 0;
        filteredEntry[service] = serviceCount;
        // Only sum to total_count if "all" is NOT selected
        if (!selectedTimelineService.includes('all')) {
          filteredEntry.total_count += serviceCount;
        }
      });
      
      return filteredEntry;
    });
    
    setTimelineData(filteredTimeline);
  }, [rawTimelineData, selectedTimelinePeriod, selectedTimelineService]);

  // Widget 5: Load Sankey Chart Data - Independent, depends on inventory and filters
  useEffect(() => {
    if (!inventoryData?.resources) {
      setLoadingSankey(false);
      return;
    }

    const processSankeyData = () => {
      try {
        setLoadingSankey(true);

    const resources = inventoryData.resources || [];
    
    // Apply filters to resources for Sankey chart only
    const filteredResources = resources.filter(resource => {
      const matchesAccount = selectedAccount.includes('all') || selectedAccount.includes(resource.account || '');
      const matchesRegion = selectedRegion.includes('all') || selectedRegion.includes(resource.region || '');
      // Service filter: handle "all" option or specific service selection
      const matchesService = selectedService.includes('all') || selectedService.length === 0 || selectedService.includes(resource.service || '');
      
      // Handle tag filtering - resource must have all selected tags
      const matchesTags = selectedTags.length === 0 || selectedTags.every(tagDisplay => {
        // Parse "key: value" display string back to key and value
        const [key, ...valueParts] = tagDisplay.split(': ');
        const value = valueParts.join(': '); // Handle values that contain ': '
        return resource.tags && resource.tags[key] === value;
      });
      
      return matchesAccount && matchesRegion && matchesService && matchesTags;
    });

    // Process Sankey data with filtered resources
    const nodes: Array<{ name: string; color?: string; regionBreakdown?: Record<string, number> }> = [];
    const links: Array<{ source: number; target: number; value: number }> = [];
    
    // Add Total node
    const totalIndex = nodes.length;
    nodes.push({ name: 'Total' });
    
    // Calculate account stats from filtered resources
    const accountStats: Record<string, number> = {};
    const accountRegionStats: Record<string, Record<string, number>> = {};
    const accountServiceStats: Record<string, Record<string, number>> = {};
    
    filteredResources.forEach(resource => {
      const account = resource.account || 'Unknown';
      const region = resource.region || 'Unknown';
      const service = resource.service || 'Unknown';
      
      accountStats[account] = (accountStats[account] || 0) + 1;
      
      if (!accountRegionStats[account]) accountRegionStats[account] = {};
      accountRegionStats[account][region] = (accountRegionStats[account][region] || 0) + 1;
      
      if (!accountServiceStats[account]) accountServiceStats[account] = {};
      accountServiceStats[account][service] = (accountServiceStats[account][service] || 0) + 1;
    });

    // Add account nodes and links
    const accountNodes: Record<string, number> = {};
    Object.entries(accountStats).forEach(([account, count]) => {
      const accountIndex = nodes.length;
      accountNodes[account] = accountIndex;
      nodes.push({ name: account });
      links.push({ source: totalIndex, target: accountIndex, value: count });
    });

    // Add region nodes and links
    const regionNodes: Record<string, number> = {};
    const regionStats: Record<string, number> = {};
    const regionServiceStats: Record<string, Record<string, number>> = {};
    
    Object.entries(accountRegionStats).forEach(([account, regions]) => {
      Object.entries(regions).forEach(([region, count]) => {
        regionStats[region] = (regionStats[region] || 0) + count;
        
        if (!regionServiceStats[region]) regionServiceStats[region] = {};
        const accountServices = accountServiceStats[account] || {};
        Object.entries(accountServices).forEach(([service, serviceCount]) => {
          regionServiceStats[region][service] = (regionServiceStats[region][service] || 0) + serviceCount;
        });
      });
    });

    Object.entries(regionStats).forEach(([region, count]) => {
      const regionIndex = nodes.length;
      regionNodes[region] = regionIndex;
      nodes.push({ name: region });
      
      // Link regions to accounts
      Object.entries(accountRegionStats).forEach(([account, regions]) => {
        if (regions[region] && accountNodes[account] !== undefined) {
          links.push({
            source: accountNodes[account],
            target: regionIndex,
            value: regions[region]
          });
        }
      });
    });

    // Add service nodes and links if showServices is true
    if (showServices) {
      const serviceStats: Record<string, number> = {};
      const serviceRegionStats: Record<string, Record<string, number>> = {};
      
      Object.entries(accountServiceStats).forEach(([account, services]) => {
        Object.entries(services).forEach(([service, count]) => {
          serviceStats[service] = (serviceStats[service] || 0) + count;
          
          if (!serviceRegionStats[service]) serviceRegionStats[service] = {};
          const accountRegions = accountRegionStats[account] || {};
          Object.entries(accountRegions).forEach(([region, regionCount]) => {
            serviceRegionStats[service][region] = (serviceRegionStats[service][region] || 0) + regionCount;
          });
        });
      });

      // Group smaller services into "Other" category
      const allServiceEntries = Object.entries(serviceStats).sort(([,a], [,b]) => b - a);
      const totalServiceResources = Object.values(serviceStats).reduce((sum, count) => sum + count, 0);
      const threshold = Math.max(1, Math.floor(totalServiceResources * 0.05));
      const majorServices: Array<[string, number]> = [];
      const minorServices: Array<[string, number]> = [];
      let otherServicesCount = 0;

      allServiceEntries.forEach(([service, count]) => {
        if (count >= threshold && majorServices.length < 8) {
          majorServices.push([service, count]);
        } else {
          minorServices.push([service, count]);
          otherServicesCount += count;
        }
      });

      const serviceNodes: Record<string, number> = {};
      
      majorServices.forEach(([service, count]) => {
        const serviceName = service.length > 12 ? service.substring(0, 12) + '...' : service;
        const serviceIndex = nodes.length;
        const regionBreakdown = serviceRegionStats[service] || {};
        nodes.push({ name: serviceName, regionBreakdown });
        serviceNodes[service] = serviceIndex;
      });

      if (minorServices.length > 0) {
        const otherIndex = nodes.length;
        const otherRegionBreakdown: Record<string, number> = {};
        minorServices.forEach(([service, count]) => {
          const serviceRegions = serviceRegionStats[service] || {};
          Object.entries(serviceRegions).forEach(([region, regionCount]) => {
            otherRegionBreakdown[region] = (otherRegionBreakdown[region] || 0) + regionCount;
          });
        });
        nodes.push({ name: `Other (${minorServices.length})`, regionBreakdown: otherRegionBreakdown });
        serviceNodes['__OTHER__'] = otherIndex;
      }

      // Create links from regions to services
      const serviceEntries = majorServices;
      if (minorServices.length > 0) {
        serviceEntries.push(['__OTHER__', otherServicesCount]);
      }

      Object.entries(regionStats).forEach(([region, regionCount]) => {
        serviceEntries.forEach(([service, serviceCount]) => {
          const totalServiceCount = Object.values(serviceStats).reduce((sum, count) => sum + count, 0);
          const proportion = serviceCount / totalServiceCount;
          const linkValue = Math.round(regionCount * proportion);

          if (linkValue > 0) {
            const targetService = service === '__OTHER__' ? '__OTHER__' : service;
            if (serviceNodes[targetService] !== undefined) {
              links.push({
                source: regionNodes[region],
                target: serviceNodes[targetService],
                value: linkValue
              });
            }
          }
        });
      });
    }

        setSankeyData({ nodes, links });
      } catch (error) {
        console.error('Error processing Sankey data:', error);
      } finally {
        setLoadingSankey(false);
      }
    };

    processSankeyData();
  }, [inventoryData, selectedAccount, selectedRegion, selectedService, selectedTags, showServices]);

  // Process tag value distribution when a tag key is selected
  useEffect(() => {
    if (!selectedTagKey || !inventoryData?.resources) {
      setTagValueDistributionData([]);
      return;
    }

    const resources = inventoryData.resources || [];
    const valueCounts: Record<string, number> = {};

    resources.forEach(resource => {
      if (resource.tags && resource.tags[selectedTagKey]) {
        const value = resource.tags[selectedTagKey];
        valueCounts[value] = (valueCounts[value] || 0) + 1;
      }
    });

    const distribution = Object.entries(valueCounts)
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);

    setTagValueDistributionData(distribution);
  }, [selectedTagKey, inventoryData]);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'rules':
        navigate('/rules');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'naming':
        navigate('/inventory?view=naming');
        break;
      case 'accounts':
        navigate('/aws-accounts');
        break;
      default:
        console.warn('Unknown quick action:', action);
    }
  };

  const handleRefresh = async () => {
    setLastRefresh(new Date());
    // Refresh all cached data
    try {
      await Promise.all([
        refreshInventory(),
        refreshServiceConfig(),
        refreshAccounts()
      ]);
    } catch (error) {
      // Error handling is done in the contexts
    }
  };

  const handleTagKeyClick = (tagKey: string) => {
    setSelectedTagKey(tagKey);
    // Scroll to Tag Value Distribution section
    if (tagValueDistributionRef.current) {
      tagValueDistributionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
            Overview
          </Typography>
          <Typography variant="h6" color="text.secondary">
            AWS Resource Tagging Management Overview
          </Typography>
        </Box>
        <Tooltip title="Refresh data">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Tagged Resources
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loadingStats || inventoryLoading ? '...' : dashboardStats.taggedResources.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {loadingStats || inventoryLoading ? '...' : `of ${dashboardStats.totalResources.toLocaleString()} total`}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 56, height: 56 }}>
                  <CheckCircleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    AWS Accounts
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: !loadingStats && !configLoading.accounts && dashboardStats.maxAccountsAllowed !== null && 
                             dashboardStats.accountsConnected >= dashboardStats.maxAccountsAllowed 
                        ? 'error.main' 
                        : 'text.primary'
                    }}
                  >
                    {loadingStats || configLoading.accounts ? '...' : (
                      dashboardStats.maxAccountsAllowed !== null 
                        ? `${dashboardStats.accountsConnected}/${dashboardStats.maxAccountsAllowed}`
                        : dashboardStats.accountsConnected
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {loadingStats || configLoading.accounts ? '...' : dashboardStats.maxAccountsAllowed !== null 
                      ? (dashboardStats.accountsConnected >= dashboardStats.maxAccountsAllowed 
                          ? 'Account limit reached' 
                          : `${dashboardStats.regionsMonitored} regions`)
                      : `${dashboardStats.regionsMonitored} regions`}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 56, height: 56 }}>
                  <CloudIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Rules
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loadingStats ? '...' : dashboardStats.totalRules}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {loadingStats ? '...' : `${dashboardStats.activeRules} active`}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                  <RuleIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Built-in Templates
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {loadingStats ? '...' : dashboardStats.builtInTemplates}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {loadingStats ? '...' : `${dashboardStats.customTemplates} custom`}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 56, height: 56 }}>
                  <AssessmentIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>

          {/* Resource Categories Treemap */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Service Resource Breakdown
                {!loadingTreemap && treemapData.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({treemapData.length} services total)
                  </Typography>
                )}
              </Typography>
              {loadingTreemap ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                </Box>
              ) : treemapData.length > 0 ? (
                <>
                  <style>
                    {`
                      .recharts-treemap text {
                        font-family: "Roboto", "Helvetica", "Arial", sans-serif !important;
                        font-size: 14px !important;
                        font-weight: bold !important;
                        fill: black !important;
                        color: black !important;
                        text-shadow: none !important;
                        stroke: none !important;
                        stroke-width: 0 !important;
                        -webkit-font-smoothing: antialiased !important;
                        -moz-osx-font-smoothing: grayscale !important;
                        text-rendering: optimizeLegibility !important;
                        shape-rendering: geometricPrecision !important;
                      }
                    `}
                  </style>
                  <Box sx={{ 
                    height: 480,
                    '& svg text': {
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif !important',
                      fontSize: '14px !important',
                      fontWeight: 'bold !important',
                      fill: 'black !important',
                      color: 'black !important',
                      textShadow: 'none !important',
                      stroke: 'none !important',
                      strokeWidth: '0 !important',
                      WebkitFontSmoothing: 'antialiased !important',
                      MozOsxFontSmoothing: 'grayscale !important',
                      textRendering: 'optimizeLegibility !important',
                      shapeRendering: 'geometricPrecision !important'
                    }
                  }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={treemapData}
                      dataKey="size"
                      aspectRatio={4/3}
                      stroke="#fff"
                    >
                      {/* Treemap visualization for resource categories */}
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box sx={{ 
                                bgcolor: 'background.paper', 
                                p: 2, 
                                borderRadius: 2, 
                                boxShadow: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                minWidth: 200
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                  <Box
                                    component="img"
                                    src={data.icon}
                                    sx={{ 
                                      width: 20, 
                                      height: 20,
                                      borderRadius: 0.5,
                                      objectFit: 'contain'
                                    }}
                                  />
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {data.name}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                  {data.size.toLocaleString()} resources
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {((data.size / treemapData.reduce((sum, item) => sum + item.size, 0)) * 100).toFixed(1)}% of total
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </Treemap>
                  </ResponsiveContainer>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No resources found</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Scan Schedule - Smaller Widget */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Scan Schedule
                </Typography>
                <Chip 
                  label={loadingStats ? '...' : dashboardStats.scanStatus} 
                  color={dashboardStats.scanStatus === 'enabled' ? 'success' : dashboardStats.scanStatus === 'disabled' ? 'default' : 'info'}
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <ScheduleIcon color="primary" sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Schedule
                      </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {loadingStats ? '...' : dashboardStats.scanSchedule}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon color="primary" sx={{ fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Status
                      </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {loadingStats ? '...' : dashboardStats.scanStatus.charAt(0).toUpperCase() + dashboardStats.scanStatus.slice(1)}
                      </Typography>
                    </Box>
                  </Box>
              </Box>
              <Stack spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<ScheduleIcon />}
                  onClick={() => navigate('/settings?tab=system-settings')}
                  fullWidth
                >
                  Configure
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => navigate('/settings?tab=system-settings')}
                  fullWidth
                >
                  View Settings
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Actions
              </Typography>
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<RuleIcon />}
                  onClick={() => handleQuickAction('rules')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Configure Rules
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<SearchIcon />}
                  onClick={() => handleQuickAction('inventory')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Inventory
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={() => handleQuickAction('accounts')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Add AWS Account
                </Button>
              </Stack>
            </CardContent>
          </Card>

        </Grid>
      </Grid>

      {/* Resource Distribution Flow Sankey Chart - Full Width */}
      <Card sx={{ mb: 3, mt: 0 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Resource Distribution Flow
              {!loadingSankey && !inventoryLoading && sankeyData.nodes.length > 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  ({showServices ? 'Total → Accounts → Regions → Services' : 'Total → Accounts → Regions'})
                </Typography>
              )}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={showServices ? 'Hide Services (3-column view)' : 'Show Services (4-column view)'}>
                <IconButton 
                  onClick={() => setShowServices(!showServices)}
                  size="small"
                  sx={{ 
                    color: showServices ? 'primary.main' : 'text.secondary',
                    bgcolor: showServices ? 'primary.50' : 'transparent'
                  }}
                >
                  {showServices ? <ViewColumnIcon /> : <ViewListIcon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="View in fullscreen">
                <IconButton 
                  onClick={() => setSankeyFullscreen(true)}
                  size="small"
                  sx={{ color: 'primary.main' }}
                >
                  <FullscreenIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Resource Distribution Flow Filters */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'text.secondary' }}>
              Filter Resources
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={['all', ...availableAccounts.map(acc => acc.name)]}
                  value={selectedAccount}
                  onChange={(_, newValue) => {
                    // Handle "all" selection logic
                    if (newValue.includes('all') && newValue.length === 1) {
                      // If only "all" is selected, keep it
                      setSelectedAccount(['all']);
                    } else {
                      // If specific values are selected (with or without "all"), remove "all" and keep only specific values
                      setSelectedAccount(newValue.filter(v => v !== 'all'));
                    }
                  }}
                  getOptionLabel={(option) => option === 'all' ? 'All Accounts' : option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AWS Account"
                      placeholder="Search accounts..."
                      size="small"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={['all', ...availableRegions]}
                  value={selectedRegion}
                  onChange={(_, newValue) => {
                    // Handle "all" selection logic
                    if (newValue.includes('all') && newValue.length === 1) {
                      // If only "all" is selected, keep it
                      setSelectedRegion(['all']);
                    } else {
                      // If specific values are selected (with or without "all"), remove "all" and keep only specific values
                      setSelectedRegion(newValue.filter(v => v !== 'all'));
                    }
                  }}
                  getOptionLabel={(option) => option === 'all' ? 'All Regions' : option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Region"
                      placeholder="Search regions..."
                      size="small"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={['all', ...availableServices]}
                  value={selectedService}
                  onChange={(_, newValue) => {
                    // Handle "all" selection logic
                    if (newValue.includes('all') && newValue.length === 1) {
                      // If only "all" is selected, keep it
                      setSelectedService(['all']);
                    } else {
                      // If specific values are selected (with or without "all"), remove "all" and keep only specific values
                      setSelectedService(newValue.filter(v => v !== 'all'));
                    }
                  }}
                  getOptionLabel={(option) => option === 'all' ? 'All Services' : option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Service"
                      placeholder="Search services..."
                      size="small"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Autocomplete
                  multiple
                  options={availableTags.map(tag => tag.display)}
                  value={selectedTags}
                  onChange={(_, newValue) => {
                    setSelectedTags(newValue);
                  }}
                  getOptionLabel={(option) => option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tag"
                      placeholder="Search tags..."
                      size="small"
                    />
                  )}
                  fullWidth
                  renderOption={(props, option) => {
                    // Find the tag object to display key and value separately
                    const tag = availableTags.find(t => t.display === option);
                    return (
                      <Box component="li" {...props}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {tag?.key || option.split(': ')[0]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          : {tag?.value || option.split(': ').slice(1).join(': ')}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          {loadingSankey || inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : sankeyData.nodes.length > 1 ? (
            <D3SankeyChart data={sankeyData} width={1200} height={400} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">No account data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Tag Analytics Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Tag Key Distribution - Bar Chart */}
        <Grid item xs={12} md={12}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Tag Key Distribution
                {!loadingTagKeyDistribution && !inventoryLoading && tagKeyDistributionData.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({tagKeyDistributionData.length} unique keys)
                  </Typography>
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most commonly used tag keys across all resources. Click on any bar to view its value distribution below.
              </Typography>
              {loadingTagKeyDistribution || inventoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                </Box>
              ) : tagKeyDistributionData.length > 0 ? (
                <Box sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={tagKeyDistributionData.slice(0, 15)}
                      layout="vertical"
                      margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis 
                        dataKey="key" 
                        type="category" 
                        width={90}
                        tick={{ fontSize: 12 }}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <Box sx={{
                                bgcolor: 'background.paper',
                                p: 1.5,
                                borderRadius: 1,
                                boxShadow: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                minWidth: 200
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                  {data.key}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Resources: {data.count.toLocaleString()}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Unique values: {data.uniqueValues}
                                </Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar 
                        dataKey="count" 
                        fill="#1976d2" 
                        radius={[0, 4, 4, 0]}
                        onClick={(data: any, index?: number) => {
                          // Extract tag key from the clicked bar's data
                          // Recharts passes the data point object as the first parameter
                          const tagKey = data?.key || (typeof index === 'number' && index >= 0 ? tagKeyDistributionData[index]?.key : null);
                          if (tagKey) {
                            handleTagKeyClick(tagKey);
                          }
                        }}
                        style={{ cursor: 'pointer' }}
                      >
                        {tagKeyDistributionData.slice(0, 15).map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={entry.key === selectedTagKey 
                              ? `hsl(${(index * 30) % 360}, 70%, 60%)` 
                              : `hsl(${(index * 30) % 360}, 70%, 50%)`} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No tag key data available
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tag Value Distribution for Specific Keys */}
      <Card ref={tagValueDistributionRef} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Tag Value Distribution
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a tag key to see the distribution of values across resources
          </Typography>
          
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={tagKeyDistributionData.map(item => item.key)}
              value={selectedTagKey}
              onChange={(_, newValue) => {
                setSelectedTagKey(newValue || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Tag Key"
                  placeholder="Choose a tag key to analyze..."
                  size="small"
                  fullWidth
                />
              )}
              sx={{ maxWidth: 400 }}
            />
          </Box>

          {selectedTagKey && (
            <>
              {inventoryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">Loading...</Typography>
                </Box>
              ) : tagValueDistributionData.length > 0 ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={tagValueDistributionData.slice(0, 20)}
                          margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="value" 
                            angle={-45}
                            textAnchor="end"
                            height={100}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis />
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Box sx={{
                                    bgcolor: 'background.paper',
                                    p: 1.5,
                                    borderRadius: 1,
                                    boxShadow: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    minWidth: 200
                                  }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {selectedTagKey}: {data.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Resources: {data.count.toLocaleString()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {((data.count / tagValueDistributionData.reduce((sum, item) => sum + item.count, 0)) * 100).toFixed(1)}% of {selectedTagKey} tags
                                    </Typography>
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                          <Bar dataKey="count" fill="#1976d2" radius={[4, 4, 0, 0]}>
                            {tagValueDistributionData.slice(0, 20).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={`hsl(${(index * 360 / Math.min(20, tagValueDistributionData.length)) % 360}, 70%, 50%)`} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ height: 400 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={tagValueDistributionData.slice(0, 10)}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ value, percent }) => `${value} (${(percent * 100).toFixed(0)}%)`}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {tagValueDistributionData.slice(0, 10).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={`hsl(${(index * 360 / Math.min(10, tagValueDistributionData.length)) % 360}, 70%, 50%)`} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <Box sx={{
                                    bgcolor: 'background.paper',
                                    p: 1.5,
                                    borderRadius: 1,
                                    boxShadow: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    minWidth: 200
                                  }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      {data.value}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {data.count.toLocaleString()} resources
                                    </Typography>
                                  </Box>
                                );
                              }
                              return null;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </Grid>
                </Grid>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    No value distribution data available for "{selectedTagKey}"
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Naming Convention - Bottom of Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            Naming Convention
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Resources by naming status
          </Typography>
          {inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : namingStatusDistributionData.length > 0 ? (
            <>
              <Box sx={{ height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={namingStatusDistributionData}
                      nameKey="label"
                      dataKey="count"
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {namingStatusDistributionData.map((entry, index) => (
                        <Cell key={`naming-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const d = payload[0].payload;
                          const total = namingStatusDistributionData.reduce((s, x) => s + x.count, 0);
                          return (
                            <Box sx={{
                              bgcolor: 'background.paper',
                              p: 1.5,
                              borderRadius: 1,
                              boxShadow: 3,
                              border: '1px solid',
                              borderColor: 'divider',
                              minWidth: 160,
                            }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                {d.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {d.count.toLocaleString()} resources
                                {total > 0 && ` (${((d.count / total) * 100).toFixed(1)}%)`}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AssignmentIcon />}
                onClick={() => handleQuickAction('naming')}
                sx={{ mt: 2 }}
              >
                View Naming Compliance
              </Button>
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No naming status data
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AssignmentIcon />}
                onClick={() => handleQuickAction('naming')}
              >
                View Inventory
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Fullscreen Sankey Chart Modal */}
      <Dialog
        open={sankeyFullscreen}
        onClose={() => setSankeyFullscreen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '95vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 0,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Resource Distribution Flow - Full View
            {!loadingSankey && !inventoryLoading && sankeyData.nodes.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({showServices ? 'Total → Accounts → Regions → Services' : 'Total → Accounts → Regions'})
              </Typography>
            )}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={showServices ? 'Hide Services (3-column view)' : 'Show Services (4-column view)'}>
              <IconButton 
                onClick={() => setShowServices(!showServices)}
                size="small"
                sx={{ 
                  color: showServices ? 'primary.main' : 'text.secondary',
                  bgcolor: showServices ? 'primary.50' : 'transparent'
                }}
              >
                {showServices ? <ViewColumnIcon /> : <ViewListIcon />}
              </IconButton>
            </Tooltip>
            <IconButton 
              onClick={() => setSankeyFullscreen(false)}
              sx={{ color: 'primary.main' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
          {loadingSankey || inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">Loading...</Typography>
            </Box>
          ) : sankeyData.nodes.length > 1 ? (
            <Box sx={{ height: '100%', p: 2 }}>
              <D3SankeyChart 
                data={sankeyData} 
                width={window.innerWidth * 0.95 - 100} 
                height={window.innerHeight * 0.95 - 200} 
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">No account data available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Resource Timeline Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Resource Count Timeline
            {!loadingTimeline && timelineData.length > 0 && (
              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                ({timelineData.length} data points)
              </Typography>
            )}
          </Typography>
          
          {/* Timeline Filters */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  multiple
                  options={['all', ...availableServices]}
                  value={selectedTimelineService}
                  onChange={(_, newValue) => {
                    // If user selects any specific service, remove the default "all" option
                    if (newValue.includes('all') && newValue.length > 1) {
                      setSelectedTimelineService(newValue.filter(v => v !== 'all'));
                      return;
                    }

                    // If user clears selection, fall back to "all"
                    if (newValue.length === 0) {
                      setSelectedTimelineService(['all']);
                      return;
                    }

                    setSelectedTimelineService(newValue);
                  }}
                  getOptionLabel={(option) => option === 'all' ? 'All Services' : option}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Service"
                      placeholder="Select services..."
                      size="small"
                    />
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={['all', 'week', '2weeks', 'month', '3months']}
                  value={selectedTimelinePeriod}
                  onChange={(_, newValue) => {
                    setSelectedTimelinePeriod(newValue || 'all');
                  }}
                  getOptionLabel={(option) => {
                    const labels: Record<string, string> = {
                      'all': 'All Time',
                      'week': 'Last Week',
                      '2weeks': 'Last 2 Weeks',
                      'month': 'Last Month',
                      '3months': 'Last 3 Months'
                    };
                    return labels[option] || option;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Time Period"
                      placeholder="Select time period..."
                      size="small"
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
          {loadingTimeline ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">Loading...</Typography>
            </Box>
          ) : timelineData.length > 0 ? (
            <Box sx={{ height: 400 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timelineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => new Date(value).toLocaleDateString()}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis />
                  <RechartsTooltip 
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box sx={{ 
                            bgcolor: 'background.paper', 
                            p: 1.5, 
                            borderRadius: 1, 
                            boxShadow: 3,
                            border: '1px solid',
                            borderColor: 'divider',
                            minWidth: 200
                          }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              {new Date(label).toLocaleDateString()}
                            </Typography>
                            {payload.map((entry: any, index: number) => (
                              <Typography 
                                key={index}
                                variant="body2" 
                                sx={{ 
                                  color: entry.color,
                                  fontWeight: 500
                                }}
                              >
                                {entry.name === 'total_count' ? 'Total Resources' : 
                                 entry.name === 'ec2' ? 'EC2' :
                                 entry.name === 's3' ? 'S3' :
                                 entry.name === 'lambda' ? 'Lambda' :
                                 entry.name === 'rds' ? 'RDS' :
                                 entry.name === 'iam' ? 'IAM' :
                                 entry.name}: {entry.value.toLocaleString()}
                              </Typography>
                            ))}
                          </Box>
                        );
                      }
                      return null;
                    }}
                  />
                  <Legend />
                  {selectedTimelineService.includes('all') && (
                    <Line 
                      type="monotone" 
                      dataKey="total_count" 
                      stroke="#1976d2" 
                      strokeWidth={3}
                      name="Total Resources"
                      dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                    />
                  )}
                  {selectedTimelineService.map((service) => {
                    // Skip "all" since we show total_count instead
                    if (service === 'all') return null;
                    
                    const colors = ['#4caf50', '#ff9800', '#9c27b0', '#f44336', '#607d8b', '#795548', '#3f51b5', '#e91e63'];
                    const index = selectedTimelineService.filter(s => s !== 'all').indexOf(service);
                    const color = colors[index % colors.length];
                    return (
                      <Line 
                        key={service}
                        type="monotone" 
                        dataKey={service} 
                        stroke={color}
                        strokeWidth={3}
                        name={service.toUpperCase()}
                        dot={{ fill: color, strokeWidth: 2, r: 4 }}
                      />
                    );
                  })}
                </LineChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary">No timeline data available</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Footer Info */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {lastRefresh.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  );
};
